import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { MapPin, Calculator, Compass, TrendingUp } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './TripCostCalculatorPage/components/CalculationForm';
import ResultsOverview from './TripCostCalculatorPage/components/ResultsOverview';
import ExpensesBreakdown from './TripCostCalculatorPage/components/ExpensesBreakdown';
import QuickAdjustmentSidebar from './TripCostCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './TripCostCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './TripCostCalculatorPage/components/TipsSidebar';
import InfoContent from './TripCostCalculatorPage/components/InfoContent';
import PopularDestinations from './TripCostCalculatorPage/components/PopularDestinations';

// Определение начального состояния формы
export const initialFormData = {
  // Основные параметры поездки
  destination: 'moscow', // Город назначения
  originCity: '', // Город отправления (для расчета транспорта)
  tripDuration: '7', // Длительность в днях
  travelers: '2', // Количество путешественников
  travelMonth: new Date().getMonth(), // Месяц поездки (для сезонных цен)
  
  // Транспорт
  transportType: 'plane', // 'plane', 'train', 'car', 'bus'
  includeReturnTicket: true, // Билет туда-обратно или только туда
  transportClass: 'economy', // 'economy', 'comfort', 'business'
  
  // Проживание
  accommodationType: 'hotel', // 'hotel', 'hostel', 'apartment', 'camping'
  accommodationRating: 3, // Класс жилья (звезд для отелей)
  accommodationLocation: 'city_center', // 'city_center', 'midtown', 'outskirts'
  includeBreakfast: true, // Включен ли завтрак
  
  // Питание
  foodOption: 'moderate', // 'budget', 'moderate', 'premium'
  selfCatering: false, // Самостоятельное приготовление еды
  includeDrinksAndAlcohol: true, // Включать ли алкоголь в бюджет
  
  // Развлечения и экскурсии
  entertainmentLevel: 'moderate', // 'minimal', 'moderate', 'extensive'
  includeGuidedTours: true, // Включать ли экскурсии с гидом
  includeMuseumsAndAttractions: true, // Музеи и достопримечательности
  
  // Дополнительные параметры
  includeShoppingBudget: true, // Бюджет на сувениры и шоппинг
  shoppingBudget: 'moderate', // 'minimal', 'moderate', 'extensive'
  includeTravelInsurance: true, // Страховка
  includeLocalTransport: true, // Местный транспорт
  includeInternationalRoaming: false, // Международный роуминг
  
  // Дополнительные опции
  includeVisaCost: false, // Стоимость визы
  travelingSeason: 'auto', // 'low', 'shoulder', 'high', 'peak', 'auto' (автоопределение)
  includeEmergencyFund: true, // Резервный фонд на непредвиденные расходы
  emergencyFundPercent: '10', // Процент от общей стоимости
};

// Определение популярных городов России
export const russianDestinations = [
  { id: 'moscow', name: 'Москва', type: 'city', costIndex: 1.3, season: { low: [0, 1, 2, 11], high: [5, 6, 7, 8], peak: [11, 0] } },
  { id: 'saint_petersburg', name: 'Санкт-Петербург', type: 'city', costIndex: 1.2, season: { low: [10, 11, 0, 1, 2], high: [4, 5, 6, 7, 8], peak: [5, 6] } },
  { id: 'sochi', name: 'Сочи', type: 'resort', costIndex: 1.4, season: { low: [9, 10, 11], high: [5, 6, 7, 8], peak: [6, 7] } },
  { id: 'kazan', name: 'Казань', type: 'city', costIndex: 0.9, season: { low: [0, 1, 2, 11], high: [4, 5, 6, 7, 8], peak: [] } },
  { id: 'yekaterinburg', name: 'Екатеринбург', type: 'city', costIndex: 0.85, season: { low: [0, 1, 2, 11], high: [5, 6, 7, 8], peak: [] } },
  { id: 'kaliningrad', name: 'Калининград', type: 'city', costIndex: 0.95, season: { low: [10, 11, 0, 1, 2], high: [5, 6, 7, 8], peak: [6, 7] } },
  { id: 'vladivostok', name: 'Владивосток', type: 'city', costIndex: 1.1, season: { low: [10, 11, 0, 1, 2], high: [6, 7, 8], peak: [7, 8] } },
  { id: 'krasnodar', name: 'Краснодар', type: 'city', costIndex: 0.9, season: { low: [0, 1, 2, 11], high: [5, 6, 7, 8], peak: [] } },
  { id: 'nizhny_novgorod', name: 'Нижний Новгород', type: 'city', costIndex: 0.8, season: { low: [0, 1, 2, 11], high: [5, 6, 7, 8], peak: [] } },
  { id: 'novosibirsk', name: 'Новосибирск', type: 'city', costIndex: 0.85, season: { low: [0, 1, 2, 11], high: [5, 6, 7, 8], peak: [] } },
  { id: 'crimea', name: 'Крым', type: 'resort', costIndex: 1.3, season: { low: [10, 11, 0, 1, 2, 3], high: [5, 6, 7, 8], peak: [6, 7] } },
  { id: 'baikal', name: 'Байкал', type: 'nature', costIndex: 1.4, season: { low: [9, 10, 11, 0, 4], high: [5, 6, 7, 8], peak: [7] } },
  { id: 'altai', name: 'Алтай', type: 'nature', costIndex: 1.2, season: { low: [9, 10, 11, 0, 1, 2, 3], high: [5, 6, 7, 8], peak: [7] } },
  { id: 'karelia', name: 'Карелия', type: 'nature', costIndex: 1.1, season: { low: [9, 10, 11, 0, 1, 2, 3], high: [5, 6, 7, 8], peak: [6, 7] } },
  { id: 'golden_ring', name: 'Золотое кольцо', type: 'route', costIndex: 1.1, season: { low: [0, 1, 2, 11], high: [4, 5, 6, 7, 8, 9], peak: [5, 6, 7] } }
];

// Определение городов-хабов (крупных транспортных узлов)
export const transportHubs = [
  { id: 'moscow', name: 'Москва' },
  { id: 'saint_petersburg', name: 'Санкт-Петербург' },
  { id: 'yekaterinburg', name: 'Екатеринбург' },
  { id: 'novosibirsk', name: 'Новосибирск' },
  { id: 'krasnodar', name: 'Краснодар' },
  { id: 'kazan', name: 'Казань' },
  { id: 'vladivostok', name: 'Владивосток' }
];

// Определение типов транспорта
export const transportTypes = [
  { id: 'plane', name: 'Самолет', iconName: 'plane', basePrice: 8000, speedFactor: 5 },
  { id: 'train', name: 'Поезд', iconName: 'train', basePrice: 4000, speedFactor: 2 },
  { id: 'car', name: 'Автомобиль', iconName: 'car', basePrice: 2500, speedFactor: 1.5 },
  { id: 'bus', name: 'Автобус', iconName: 'bus', basePrice: 2000, speedFactor: 1 }
];

// Определение типов проживания
export const accommodationTypes = [
  { id: 'hotel', name: 'Отель', basePrice: 3500 },
  { id: 'hostel', name: 'Хостел', basePrice: 800 },
  { id: 'apartment', name: 'Апартаменты', basePrice: 2500 },
  { id: 'camping', name: 'Кемпинг', basePrice: 500 }
];

// Сезоны и их влияние на цены
export const seasons = [
  { id: 'low', name: 'Низкий сезон', priceFactor: 0.7 },
  { id: 'shoulder', name: 'Межсезонье', priceFactor: 0.9 },
  { id: 'high', name: 'Высокий сезон', priceFactor: 1.2 },
  { id: 'peak', name: 'Пиковый сезон', priceFactor: 1.5 }
];

/**
 * TripCostCalculatorPage Component - Полный калькулятор стоимости путешествий с различными параметрами и дополнительными опциями.
 */
const TripCostCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('trip-cost-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор стоимости поездки | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте полную стоимость путешествия с учетом транспорта, проживания, питания, развлечений и других расходов. Планируйте бюджет поездки по России и за рубеж с детализацией всех затрат.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [expensesBreakdown, setExpensesBreakdown] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedTrips, setSavedTrips] = useState([]);
  const [isBreakdownExpanded, setIsBreakdownExpanded] = useState(false);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных поездок из localStorage ---
  useEffect(() => {
    const savedTripsData = localStorage.getItem('savedTrips');
    if (savedTripsData) {
      try {
        setSavedTrips(JSON.parse(savedTripsData));
      } catch (e) {
        console.error("Ошибка загрузки сохраненных поездок:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'moscow':
          newState.destination = 'moscow';
          break;
        case 'saint_petersburg':
          newState.destination = 'saint_petersburg';
          break;
        case 'budget_trip':
          newState.transportType = 'bus';
          newState.accommodationType = 'hostel';
          newState.foodOption = 'budget';
          newState.entertainmentLevel = 'minimal';
          newState.includeShoppingBudget = false;
          break;
        case 'family_trip':
          newState.travelers = '4';
          newState.accommodationType = 'apartment';
          newState.selfCatering = true;
          newState.includeGuidedTours = true;
          newState.includeMuseumsAndAttractions = true;
          break;
        case 'luxury_trip':
          newState.transportType = 'plane';
          newState.transportClass = 'business';
          newState.accommodationType = 'hotel';
          newState.accommodationRating = 5;
          newState.foodOption = 'premium';
          newState.entertainmentLevel = 'extensive';
          break;
        case 'beach_holiday':
          newState.destination = 'sochi';
          newState.travelMonth = 6; // Июль
          newState.foodOption = 'moderate';
          break;
        case 'cultural_trip':
          newState.destination = 'golden_ring';
          newState.includeGuidedTours = true;
          newState.includeMuseumsAndAttractions = true;
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setExpensesBreakdown([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение поездки в localStorage
  const saveTrip = (name) => {
    if (!results) return;

    const newSavedTrip = {
      id: Date.now().toString(),
      name: name || `Поездка в ${getDestinationName(formData.destination)} на ${formData.tripDuration} дней`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      destination: formData.destination
    };

    const updatedSavedTrips = [...savedTrips, newSavedTrip];
    setSavedTrips(updatedSavedTrips);
    localStorage.setItem('savedTrips', JSON.stringify(updatedSavedTrips));

    return newSavedTrip;
  };

  // Удаление сохраненной поездки
  const deleteSavedTrip = (id) => {
    const updatedSavedTrips = savedTrips.filter(trip => trip.id !== id);
    setSavedTrips(updatedSavedTrips);
    localStorage.setItem('savedTrips', JSON.stringify(updatedSavedTrips));
  };

  // Загрузка сохраненной поездки
  const loadSavedTrip = (id) => {
    const tripToLoad = savedTrips.find(trip => trip.id === id);
    if (tripToLoad) {
      setFormData(tripToLoad.formData);
      setResults(tripToLoad.results);
      
      // Вычисляем разбивку расходов на основе результатов
      if (tripToLoad.results) {
        const breakdown = calculateExpensesBreakdown(tripToLoad.results);
        setExpensesBreakdown(breakdown);
      }
    }
  };

  // Получение названия пункта назначения по ID
  const getDestinationName = (destinationId) => {
    const destination = russianDestinations.find(dest => dest.id === destinationId);
    return destination ? destination.name : 'Неизвестное место';
  };

  // Вычисление разбивки расходов на основе результатов
  const calculateExpensesBreakdown = (resultsData) => {
    if (!resultsData) return [];
    
    // Создаем массив категорий расходов
    return [
      { 
        category: 'Транспорт', 
        amount: resultsData.transportCost, 
        percentage: Math.round((resultsData.transportCost / resultsData.totalCost) * 100),
        details: [
          {
            name: 'Основной транспорт',
            amount: resultsData.transportMainCost
          },
          {
            name: 'Местный транспорт',
            amount: resultsData.localTransportCost
          }
        ]
      },
      { 
        category: 'Проживание', 
        amount: resultsData.accommodationCost, 
        percentage: Math.round((resultsData.accommodationCost / resultsData.totalCost) * 100),
        details: [
          {
            name: 'Стоимость жилья',
            amount: resultsData.accommodationBaseCost
          },
          {
            name: 'Дополнительные услуги',
            amount: resultsData.accommodationExtraCost || 0
          }
        ]
      },
      { 
        category: 'Питание', 
        amount: resultsData.foodCost, 
        percentage: Math.round((resultsData.foodCost / resultsData.totalCost) * 100),
        details: [
          {
            name: 'Основное питание',
            amount: resultsData.foodBaseCost
          },
          {
            name: 'Напитки и алкоголь',
            amount: resultsData.drinksAndAlcoholCost || 0
          }
        ]
      },
      { 
        category: 'Развлечения', 
        amount: resultsData.entertainmentCost, 
        percentage: Math.round((resultsData.entertainmentCost / resultsData.totalCost) * 100),
        details: [
          {
            name: 'Музеи и достопримечательности',
            amount: resultsData.museumsAndAttractionsCost || 0
          },
          {
            name: 'Экскурсии',
            amount: resultsData.guidedToursCost || 0
          },
          {
            name: 'Прочие развлечения',
            amount: resultsData.otherEntertainmentCost || 0
          }
        ]
      },
      { 
        category: 'Шоппинг', 
        amount: resultsData.shoppingCost || 0, 
        percentage: Math.round(((resultsData.shoppingCost || 0) / resultsData.totalCost) * 100) 
      },
      { 
        category: 'Дополнительные расходы', 
        amount: resultsData.otherCosts || 0, 
        percentage: Math.round(((resultsData.otherCosts || 0) / resultsData.totalCost) * 100),
        details: [
          {
            name: 'Страховка',
            amount: resultsData.insuranceCost || 0
          },
          {
            name: 'Резервный фонд',
            amount: resultsData.emergencyFundCost || 0
          },
          {
            name: 'Прочие расходы',
            amount: (resultsData.otherCosts || 0) - (resultsData.insuranceCost || 0) - (resultsData.emergencyFundCost || 0)
          }
        ]
      }
    ];
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <MapPin className="w-8 h-8 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор стоимости поездки'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Популярные направления --- */}
            <PopularDestinations 
              russianDestinations={russianDestinations} 
              formData={formData}
              setFormData={setFormData}
              seasons={seasons}
            />

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              setExpensesBreakdown={setExpensesBreakdown}
              russianDestinations={russianDestinations}
              transportTypes={transportTypes}
              accommodationTypes={accommodationTypes}
              transportHubs={transportHubs}
              saveTrip={saveTrip}
              calculateExpensesBreakdown={calculateExpensesBreakdown}
              seasons={seasons}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview 
                  results={results} 
                  formData={formData} 
                  getDestinationName={getDestinationName}
                />
                <ExpensesBreakdown 
                  expenses={expensesBreakdown}
                  isExpanded={isBreakdownExpanded}
                  setIsExpanded={setIsBreakdownExpanded}
                  travelers={parseInt(formData.travelers, 10)}
                />
              </>
            )}

            {/* --- Информационный контент о путешествиях --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              russianDestinations={russianDestinations}
            />

            {/* --- Панель сравнения поездок --- */}
            <ComparisonSidebar 
              savedTrips={savedTrips}
              deleteSavedTrip={deleteSavedTrip}
              loadSavedTrip={loadSavedTrip}
              getDestinationName={getDestinationName}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar */
        .expenses-breakdown-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .expenses-breakdown-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .expenses-breakdown-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .expenses-breakdown-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .expenses-breakdown-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .expenses-breakdown-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .expenses-breakdown-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .expenses-breakdown-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .expenses-breakdown-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default TripCostCalculatorPage;